import { Link } from "@tanstack/react-router";
import { m } from "framer-motion";
import { ArrowRight, Clapperboard, Film } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocale } from "@/i18n/LocaleProvider";

/**
 * 404 i Fenny-tonalitet: filmreferens (klappa, take, klippt scen) snarare
 * än generisk "Page not found". Animerad klappa som "slår igen" och en
 * countdown 3·2·1 → 404 i butter/mint på svart.
 */
export function NotFound404() {
  const locale = useLocale();
  const isEn = locale === "en";
  const [count, setCount] = useState<3 | 2 | 1 | "404">(3);

  useEffect(() => {
    const seq: Array<[3 | 2 | 1 | "404", number]> = [
      [3, 0],
      [2, 600],
      [1, 1200],
      ["404", 1800],
    ];
    const timers = seq.map(([v, t]) => window.setTimeout(() => setCount(v), t));
    return () => timers.forEach(clearTimeout);
  }, []);

  const copy = isEn
    ? {
        eyebrow: "Take 404",
        title: "This scene didn't make the final cut.",
        body: "The page you were looking for ended up on the cutting room floor — or it never got a green light to begin with. Either way: nothing to roll here.",
        cta: "Back to set",
        secondary: "Or jump straight to —",
        links: [
          { to: "/en/pulse", label: "Pulse — video subscription" },
          { to: "/en/work", label: "Selected work" },
          { to: "/en/knowledge", label: "Knowledge library" },
          { to: "/en/contact", label: "Talk to a producer" },
        ],
        slate: { scene: "SCENE", take: "TAKE", roll: "ROLL", director: "DIR" },
      }
    : {
        eyebrow: "Tagning 404",
        title: "Den här scenen klippte vi bort.",
        body: "Sidan du letar efter hamnade på klipprummets golv — eller fick aldrig grönt ljus från första början. Hursomhelst: inget att rulla här.",
        cta: "Tillbaka till inspelningsplatsen",
        secondary: "Eller hoppa direkt till —",
        links: [
          { to: "/pulse", label: "Pulse — filmabonnemang" },
          { to: "/case", label: "Utvalda case" },
          { to: "/kunskap", label: "Kunskapsbiblioteket" },
          { to: "/kontakt", label: "Prata med en producent" },
        ],
        slate: { scene: "SCEN", take: "TAGNING", roll: "RULLE", director: "REGI" },
      };

  return (
    <section className="relative isolate flex min-h-screen items-center overflow-hidden bg-background px-4 pt-28 pb-16 md:pt-32">
      {/* Filmkorn + vinjett */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(0,0,0,0.6)_100%)]"
      />
      {/* Sprocket-hål uppe + nere som en filmremsa */}
      <FilmStrip className="absolute inset-x-0 top-20 hidden md:block" />
      <FilmStrip className="absolute inset-x-0 bottom-8 hidden md:block" reverse />

      <div className="container-wide relative z-10 mx-auto grid w-full max-w-6xl gap-12 md:grid-cols-[1.2fr_1fr] md:items-center">
        {/* Vänster: clapperboard + copy */}
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-mint/30 bg-mint/5 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-mint">
            <Film className="h-3 w-3" /> {copy.eyebrow}
          </div>

          <h1 className="mt-6 text-balance text-4xl font-semibold leading-[1.02] text-butter md:text-6xl">
            {copy.title}
          </h1>
          <p className="mt-5 max-w-xl text-pretty text-base text-foreground/75 md:text-lg">
            {copy.body}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={isEn ? "/en" : "/"}
              className="inline-flex items-center gap-2 rounded-full bg-mint px-6 py-3 text-sm font-medium text-mint-foreground transition-transform hover:-translate-y-0.5"
            >
              {copy.cta}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-10">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {copy.secondary}
            </div>
            <ul className="mt-3 flex flex-col gap-1">
              {copy.links.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="group inline-flex items-center gap-2 text-foreground/85 transition-colors hover:text-mint"
                  >
                    <span className="font-medium">{l.label}</span>
                    <ArrowRight className="h-3.5 w-3.5 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Höger: animerad clapperboard */}
        <div className="relative mx-auto w-full max-w-md">
          <Clapper count={count} slate={copy.slate} />
        </div>
      </div>
    </section>
  );
}

function Clapper({
  count,
  slate,
}: {
  count: 3 | 2 | 1 | "404";
  slate: { scene: string; take: string; roll: string; director: string };
}) {
  return (
    <div className="relative aspect-[4/3] w-full">
      {/* Skugga */}
      <div className="absolute inset-x-6 bottom-2 h-6 rounded-full bg-black/60 blur-2xl" />
      {/* Clapper-kropp */}
      <div className="absolute inset-0 rounded-[20px] border border-white/10 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black p-5 shadow-2xl">
        {/* Övre del — den som "slår igen" */}
        <m.div
          initial={{ rotate: -22, transformOrigin: "0% 100%" }}
          animate={{ rotate: [-22, -22, 0, 0, -10, 0] }}
          transition={{
            duration: 2.2,
            times: [0, 0.6, 0.72, 0.85, 0.92, 1],
            ease: "easeOut",
          }}
          className="relative h-[34%] overflow-hidden rounded-t-[14px] border border-white/15 bg-zinc-950"
        >
          {/* Diagonala mint/butter-striper */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "repeating-linear-gradient(115deg, #FFFDB6 0 28px, #000 28px 56px, #42FFC9 56px 84px, #000 84px 112px)",
            }}
          />
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-[10px] font-bold uppercase tracking-[0.3em] text-black mix-blend-screen">
            {/* Slate-titel sitter i panelen nedan */}
          </div>
        </m.div>

        {/* Slate-yta */}
        <div className="mt-2 grid h-[64%] grid-rows-[auto_1fr_auto] rounded-b-[14px] border border-white/10 bg-zinc-900/80 p-4 text-foreground">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Clapperboard className="h-3 w-3 text-mint" /> Fenny · Stockholm
            </span>
            <span className="text-mint">REC</span>
          </div>

          {/* Stora siffrorna */}
          <div className="flex items-center justify-center">
            <m.div
              key={String(count)}
              initial={{ scale: 0.6, opacity: 0, filter: "blur(6px)" }}
              animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="font-display text-[88px] font-bold leading-none text-butter md:text-[112px]"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {count}
            </m.div>
          </div>

          {/* Slate-meta */}
          <div className="grid grid-cols-4 gap-2 border-t border-white/10 pt-3 text-[10px] uppercase tracking-[0.18em]">
            <SlateField label={slate.scene} value="404" />
            <SlateField label={slate.take} value="03" />
            <SlateField label={slate.roll} value="A" />
            <SlateField label={slate.director} value="—" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SlateField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-sm text-mint">{value}</div>
    </div>
  );
}

function FilmStrip({
  className = "",
  reverse = false,
}: {
  className?: string;
  reverse?: boolean;
}) {
  return (
    <m.div
      aria-hidden
      initial={{ x: reverse ? 0 : -200 }}
      animate={{ x: reverse ? -200 : 0 }}
      transition={{
        duration: 30,
        ease: "linear",
        repeat: Infinity,
        repeatType: "loop",
      }}
      className={`flex h-6 items-center gap-3 ${className}`}
    >
      {Array.from({ length: 40 }).map((_, i) => (
        <span
          key={i}
          className="h-4 w-6 shrink-0 rounded-[2px] border border-white/10 bg-white/[0.04]"
        />
      ))}
    </m.div>
  );
}
